<template>
  <transition
    name="zm-drawer-fade"
    @after-enter="afterEnter"
    @after-leave="afterLeave"
  >
    <div
      class="zm-drawer__wrapper"
      tabindex="-1"
      v-show="visible"
      :style="wrapperStyl"
    >
      <div
        class="zm-drawer__container"
        :class="visible && 'zm-drawer__open'"
        @click.self="handleWrapperClick"
        role="document"
        tabindex="-1"
      >
        <div
          aria-modal="true"
          aria-labelledby="zm-drawer__title"
          :aria-label="title"
          class="zm-drawer"
          :class="[direction, customClass]"
          ref="drawer"
          role="dialog"
          tabindex="-1"
          :style="drawerStyl"
        >
          <header class="zm-drawer__header" id="zm-drawer__title" v-if="withHeader">
            <slot name="title">
              <span role="heading" :title="title">{{ title }}</span>
            </slot>
            <button
              :aria-label="`close ${title || 'drawer'}`"
              class="zm-drawer__close-btn"
              type="button"
              v-if="showClose"
              @click="closeDrawer"
            >
              <i class="zm-dialog__close zm-icon zm-icon-close"></i>
            </button>
          </header>
          <section class="zm-drawer__body" v-if="rendered">
            <slot></slot>
          </section>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
  import Popup from '../../utils/popup';
  import emitter from '../../mixins/emitter';
  import AriaDialog from '../../utils/aria-dialog';

  export default {
    name: 'ZmDrawer',
    mixins: [Popup, emitter],
    props: {
      appendToBody: Boolean,
      beforeClose: {
        type: Function
      },
      customClass: {
        type: String,
        default: ''
      },
      closeOnPressEscape: {
        type: Boolean,
        default: true
      },
      destroyOnClose: Boolean,
      modal: Boolean,
      direction: {
        type: String,
        default: 'rtl',
        validator(val) {
          return ['ltr', 'rtl', 'ttb', 'btt'].indexOf(val) !== -1;
        }
      },
      modalAppendToBody: {
        type: Boolean,
        default: true
      },
      showClose: {
        type: Boolean,
        default: true
      },
      size: {
        type: [Number, String],
        default: '30%'
      },
      title: {
        type: String,
        default: ''
      },
      visible: Boolean,
      wrapperClosable: {
        type: Boolean,
        default: true
      },
      withHeader: {
        type: Boolean,
        default: true
      },
      boxShadow: {
        type: Boolean,
        default: true
      },
      outerClickable: Boolean,
      renderInPlace: Boolean
    },
    computed: {
      isHorizontal() {
        return this.direction === 'rtl' || this.direction === 'ltr';
      },
      drawerSize() {
        return typeof this.size === 'number' ? `${this.size}px` : this.size;
      },
      wrapperStyl() {
        const styl = {};
        if (this.renderInPlace) {
          styl.position = 'absolute';
        }
        if (this.outerClickable) {
          if (this.isHorizontal) {
            styl.width = this.drawerSize;
          } else {
            styl.height = this.drawerSize;
          }
          const directionMap = {
            'ltr': 'right',
            'rtl': 'left',
            'ttb': 'bottom',
            'btt': 'top'
          };
          styl[directionMap[this.direction]] = 'auto';
        }
        return styl;
      },
      drawerStyl() {
        const styl = {};
        if (this.boxShadow) {
          styl.boxShadow = `0 8px 10px -5px rgba(0, 0, 0, 0.2),
    0 16px 24px 2px rgba(0, 0, 0, 0.14),
    0 6px 30px 5px rgba(0, 0, 0, 0.12)`;
        } else {
          const directionMap = {
            'ltr': 'Right',
            'rtl': 'Left',
            'ttb': 'Bottom',
            'btt': 'Top'
          };
          styl[`border${directionMap[this.direction]}`] = '1px solid #EAEAF0';
        }
        let size = this.drawerSize;
        if (this.outerClickable) {
          // consider the box shadow width
          size = this.boxShadow ? 'calc(100% - 42px)' : '100%';
        }
        if (this.isHorizontal) {
          styl.width = size;
        } else {
          styl.height = size;
        }
        return styl;
      }
    },
    data() {
      return {
        closed: false,
        prevActiveElement: null
      };
    },
    watch: {
      visible(val) {
        if (val) {
          this.closed = false;
          this.$emit('open');
          if (this.appendToBody) {
            document.body.appendChild(this.$el);
          }
          this.prevActiveElement = document.activeElement;
        } else {
          if (!this.closed) this.$emit('close');
          this.$nextTick(() => {
            if (this.prevActiveElement) {
              this.prevActiveElement.focus();
            }
          });
        }
      }
    },
    methods: {
      getFirstFocus() {
        const focusFirst = this.$el.querySelector('[focusFirst]') || this.$el.querySelector('[focus-first]');
        if (focusFirst) {
          return focusFirst;
        }
        return null;
      },
      afterEnter() {
        this.$emit('opened');
        const focusAfterClosed = document.activeElement;
        const firstFocus = this.getFirstFocus();
        this.$nextTick(() => {
          this.ariaDialog = new AriaDialog(this.$refs.drawer, focusAfterClosed, firstFocus);
        });
      },
      afterLeave() {
        this.$emit('closed');
        this.ariaDialog && this.ariaDialog.closeDialog();
        this.ariaDialog = null;
      },
      hide(cancel) {
        if (cancel !== false) {
          this.$emit('update:visible', false);
          this.$emit('close');
          if (this.destroyOnClose === true) {
            this.rendered = false;
          }
          this.closed = true;
        }
      },
      handleWrapperClick() {
        if (this.wrapperClosable) {
          this.closeDrawer();
        }
      },
      closeDrawer() {
        if (typeof this.beforeClose === 'function') {
          this.beforeClose(this.hide);
        } else {
          this.hide();
        }
      },
      handleClose() {
        // This method here will be called by PopupManger, when the `closeOnPressEscape` was set to true
        // pressing `ESC` will call this method, and also close the drawer.
        // This method also calls `beforeClose` if there was one.
        this.closeDrawer();
      }
    },
    mounted() {
      if (this.visible) {
        this.rendered = true;
        this.open();
      }
    },
    destroyed() {
      // if appendToBody is true, remove DOM node after destroy
      if (this.appendToBody && this.$el && this.$el.parentNode) {
        this.$el.parentNode.removeChild(this.$el);
      }
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";

  @keyframes zm-drawer-fade-in {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @mixin drawer-animation($direction) {
    @keyframes #{$direction}-drawer-in {
      0% {

        @if $direction == ltr {
          transform: translate(-100%, 0px);
        }

        @if $direction == rtl {
          transform: translate(100%, 0px);
        }

        @if $direction == ttb {
          transform: translate(0px, -100%);
        }

        @if $direction == btt {
          transform: translate(0px, 100%);
        }
      }

      100% {
        @if $direction == ltr {
          transform: translate(0px, 0px);
        }

        @if $direction == rtl {
          transform: translate(0px, 0px);
        }

        @if $direction == ttb {
          transform: translate(0px, 0px);
        }

        @if $direction == btt {
          transform: translate(0px, 0px);
        }
      }
    }

    @keyframes #{$direction}-drawer-out {
      0% {
        @if $direction == ltr {
          transform: translate(0px, 0px);
        }

        @if $direction == rtl {
          transform: translate(0px, 0px);;
        }

        @if $direction == ttb {
          transform: translate(0px, 0px);
        }

        @if $direction == btt {
          transform: translate(0px, 0);
        }
      }

      100% {
        @if $direction == ltr {
          transform: translate(-100%, 0px);
        }

        @if $direction == rtl {
          transform: translate(100%, 0px);
        }

        @if $direction == ttb {
          transform: translate(0px, -100%);
        }

        @if $direction == btt {
          transform: translate(0px, 100%);
        }
      }
    }
  }

  @mixin animation-in($direction) {
    .zm-drawer__open &.#{$direction} {
      animation: #{$direction}-drawer-in .3s 1ms;
    }
  }

  @mixin animation-out($direction) {
    &.#{$direction} {
      animation: #{$direction}-drawer-out .3s;
    }
  }

  @include drawer-animation(rtl);
  @include drawer-animation(ltr);
  @include drawer-animation(ttb);
  @include drawer-animation(btt);

  $directions: rtl, ltr, ttb, btt;

  @include b(drawer) {
    position: absolute;
    box-sizing: border-box;
    background-color: $--dialog-background-color;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    outline: 0;

    @each $direction in $directions {
      @include animation-out($direction);
      @include animation-in($direction);
    }

    &__wrapper {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      overflow: hidden;
      margin: 0;
    }

    &__header {
      align-items: center;
      color: $--color-text-primary;
      font-size: 20px;
      display: flex;
      margin-bottom: 32px;
      padding: 20px;
      padding-bottom: 0;
      & > :first-child {
        flex: 1;
      }
    }

    &__title {
      margin: 0;
      flex: 1;
      line-height: inherit;
      font-size: 1rem;
    }

    &__close-btn {
      border: none;
      cursor: pointer;
      font-size: $--font-size-large;
      color: inherit;
      background-color: transparent;
    }

    &__body {
      flex: 1;
      overflow: auto;
      & > * {
        box-sizing: border-box;
      }
    }

    &.ltr, &.rtl {
      height: 100%;
      top: 0;
      bottom: 0;
    }

    &.ttb, &.btt {
      width: 100%;
      left: 0;
      right: 0;
    }

    &.ltr {
      left: 0;
    }

    &.rtl {
      right: 0;
    }

    &.ttb {
      top: 0;
    }

    &.btt {
      bottom: 0;
    }
  }

  .zm-theme--dark{
    @include b(drawer) {
      background-color: #252A30;
    }
  }
  .zm-drawer__container {
    position: relative;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    height: 100%;
    width: 100%;
  }

  .zm-drawer-fade-enter-active {
    animation: zm-drawer-fade-in .3s;
  }

  .zm-drawer-fade-leave-active {
    animation: zm-drawer-fade-in .3s reverse;
  }
</style>
